import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Divider,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  Select,
  MenuItem
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../ConfirmDialog';
import api, { resetProductSyncSettings, getProductSyncStatus } from '../../services/api';

export default function SettingsTab() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [errors, setErrors] = useState({});
  const [settings, setSettings] = useState({
    getItemsEndpoint: '',
    getItemImageEndpoint: '',
    image_category: '',
    image_max_width: '',
    image_max_height: '',
    product_variant_name: '',
    query_batch_size: '',
    erp_query_columns: '',
    whole_items_query: '',
    updated_items_query: '',
    product_notes_query: '',
    document_endpoint: '',
    product_tags_query: '',
    description_note_seq: '',
    extra_configurations: []
  });
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [descriptionNoteSeqError, setDescriptionNoteSeqError] = useState('');

  // Sync status
  const { data: syncStatusData, isLoading: isLoadingSyncStatus } = useQuery({
    queryKey: ['productSyncSettingsStatus'],
    queryFn: async () => {
      const response = await getProductSyncStatus();
      return response.data;
    },
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: 5000 // Poll every 5 seconds
  });

  const syncInProgress = syncStatusData?.data?.isRunning || false;

  const {
    data: settingsData,
    isLoading: isLoadingSettings,
    error: settingsError
  } = useQuery({
    queryKey: ['productSyncSettings'],
    queryFn: async () => {
      const response = await api.get('/product-sync/settings');
      return response.data.data;
    },
    staleTime: 0
  });

  useEffect(() => {
    if (settingsData) {
      // Initialize extra_configurations if it doesn't exist
      const processedSettings = {
        ...settingsData,
        extra_configurations: settingsData.extra_configurations || []
      };
      setSettings(processedSettings);
      setDescriptionNoteSeqError(''); // Clear any previous validation errors
    }
  }, [settingsData]);

  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setSettings({
      ...settings,
      [name]: (name.includes('max') || name === 'query_batch_size') ? (value === '' ? '' : parseInt(value)) : value
    });

    // Validate description_note_seq format
    if (name === 'description_note_seq') {
      if (value === null || value === undefined || value === '') {
        setDescriptionNoteSeqError('');
      } else {
        // Convert to string and trim
        const stringValue = String(value).trim();
        if (stringValue === '') {
          setDescriptionNoteSeqError('');
        } else {
          const sequenceNumbers = stringValue.split(',').map(seq => seq.trim());
          const isValid = sequenceNumbers.every(seq => {
            const num = parseInt(seq);
            return !isNaN(num) && num > 0;
          });

          if (!isValid) {
            setDescriptionNoteSeqError('Please enter valid comma-separated numbers (e.g., 30,31,32)');
          } else {
            setDescriptionNoteSeqError('');
          }
        }
      }
    }
  };

  const handleExtraConfigurationChange = (index, field, value) => {
    const updatedConfigurations = [...settings.extra_configurations];
    const updatedErrors = { ...errors };

    if (field === 'note_seq_number') {
      if (value === '') {
        updatedConfigurations[index] = {
          ...updatedConfigurations[index],
          [field]: ''
        };
        delete updatedErrors[index]; // clear error if value is empty
      } else if (!isNaN(value)) {
        updatedConfigurations[index] = {
          ...updatedConfigurations[index],
          [field]: parseInt(value, 10)
        };
        delete updatedErrors[index]; // clear error if valid
      } else {
        // set error for this index
        updatedConfigurations[index] = {
          ...updatedConfigurations[index],
          [field]: value
        };
        updatedErrors[index] = 'Note sequence number must be a valid number';
      }
    } else {
      updatedConfigurations[index] = {
        ...updatedConfigurations[index],
        [field]: value
      };
    }

    setSettings({
      ...settings,
      extra_configurations: updatedConfigurations
    });

    setErrors(updatedErrors);
  };

  const handleConfirmReset = () => {
    resetSettingsMutation.mutate();
  };

  const addExtraConfiguration = () => {
    const newConfiguration = {
      note_seq_number: '',
      type: 'pdf',
      metafield_namespace: 'custom',
      metafield_key: ''
    };
    setSettings({
      ...settings,
      extra_configurations: [...settings.extra_configurations, newConfiguration]
    });
  };

  const removeExtraConfiguration = (index) => {
    const updatedConfigurations = settings.extra_configurations.filter((_, i) => i !== index);
    setSettings({
      ...settings,
      extra_configurations: updatedConfigurations
    });
  };

  const updateSettingsMutation = useMutation({
    mutationFn: async (updatedSettings) => {
      const response = await api.put('/product-sync/settings', updatedSettings);
      return response.data;
    },
    onSuccess: () => {
      toast.success('Settings updated successfully');
      queryClient.invalidateQueries({ queryKey: ['productSyncSettings'] });
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error('Cannot update settings while product sync is in progress');
      } else {
        toast.error(`Failed to update settings: ${error.response?.data?.message || error.message}`);
      }
    }
  });

  const resetSettingsMutation = useMutation({
    mutationFn: async () => {
      const response = await resetProductSyncSettings();
      return response.data;
    },
    onSuccess: () => {
      toast.success('Settings reset to default successfully');
      queryClient.invalidateQueries({ queryKey: ['productSyncSettings'] });
      setShowResetDialog(false);
    },
    onError: (error) => {
      if (error.response?.status === 423) {
        toast.error('Cannot reset settings while product sync is in progress');
      } else {
        toast.error(`Failed to reset settings: ${error.response?.data?.message || error.message}`);
      }
      setShowResetDialog(false);
    }
  });

  const handleSettingsSubmit = (e) => {
    e.preventDefault();

    // Check for validation errors
    if (descriptionNoteSeqError) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    // Convert numeric fields to proper numbers for submission
    const processedSettings = { ...settings };
    ['image_max_width', 'image_max_height', 'query_batch_size'].forEach(field => {
      if (processedSettings[field] !== '') {
        processedSettings[field] = parseInt(processedSettings[field]);
      }
    });

    updateSettingsMutation.mutate(processedSettings);
  };

  const handleResetSettings = () => {
    setShowResetDialog(true);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        {syncInProgress && isAdmin && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Product synchronization is currently in progress. Settings updates are disabled until the sync completes.
          </Alert>
        )}
        <Paper sx={{ p: '15px' }}>
          <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
            <Box display='flex' alignItems='center'>
              <SettingsIcon color='primary' sx={{ mr: 1 }} />
              <Typography variant='h6'>
                Product Sync Settings
              </Typography>
            </Box>
            {isAdmin ? (
              <Button
                variant='contained'
                color='warning'
                startIcon={<RestoreIcon />}
                onClick={handleResetSettings}
                disabled={syncInProgress || !isAdmin || isLoadingSettings || isLoadingSyncStatus}
                title={syncInProgress ? 'Cannot reset settings while sync is in progress' : 'Reset to default settings'}
              >
                Reset
              </Button>
            ) : null}
          </Box>

          {isLoadingSettings || isLoadingSyncStatus ? (
            <Box display='flex' justifyContent='center' my={4}>
              <CircularProgress />
            </Box>
          ) : settingsError ? (
            <Alert severity='error' sx={{ mb: 3 }}>
              Error loading settings: {settingsError.message}
            </Alert>
          ) : !settingsData ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <form onSubmit={handleSettingsSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant='subtitle1' gutterBottom fontWeight='medium'>
                    ERP API & Parameters
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Get Items Endpoint'
                    name='getItemsEndpoint'
                    value={settings.getItemsEndpoint}
                    onChange={handleSettingsChange}
                    fullWidth
                    required
                    variant='outlined'
                    disabled={syncInProgress || !isAdmin}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Get Item Image Endpoint'
                    name='getItemImageEndpoint'
                    value={settings.getItemImageEndpoint}
                    onChange={handleSettingsChange}
                    fullWidth
                    required
                    variant='outlined'
                    disabled={syncInProgress || !isAdmin}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Image Category'
                    name='image_category'
                    value={settings.image_category}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    variant='outlined'
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Max Image Width'
                    name='image_max_width'
                    value={settings.image_max_width}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    variant='outlined'
                    type='number'
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>px</InputAdornment>
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Max Image Height'
                    name='image_max_height'
                    value={settings.image_max_height}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    variant='outlined'
                    type='number'
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>px</InputAdornment>
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Query Batch Size'
                    name='query_batch_size'
                    value={settings.query_batch_size}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    variant='outlined'
                    type='number'
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant='subtitle1' gutterBottom fontWeight='medium' sx={{ mt: 2 }}>
                    ERP Query Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='ERP Query Columns'
                    name='erp_query_columns'
                    value={settings.erp_query_columns}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    multiline
                    rows={5}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change ERP query columns' : 'Comma-separated list of columns to fetch from ERP'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Whole Items Query'
                    name='whole_items_query'
                    value={settings.whole_items_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    multiline
                    rows={isMobile ? 5 : 3}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change whole items query' : 'Query for fetching all items from ERP. Use ${company} as a placeholder for the company ID.'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Updated Items Query'
                    name='updated_items_query'
                    value={settings.updated_items_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    multiline
                    rows={isMobile ? 5 : 4}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change updated items query' : 'Query for fetching updated items from ERP. Use ${company} as a placeholder for the company ID.'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Product Notes Query'
                    name='product_notes_query'
                    value={settings.product_notes_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    multiline
                    rows={isMobile ? 5 : 3}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change product notes query' : 'Query for fetching product notes from ERP. Use ${itemCode} and ${company} as placeholders.'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label='Product Tags Query'
                    name='product_tags_query'
                    value={settings.product_tags_query}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    multiline
                    rows={isMobile ? 3 : 2}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change product tags query' : 'Query for fetching product tags from ERP. Use ${itemCode} and ${company} as placeholders.'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant='subtitle1' gutterBottom fontWeight='medium' sx={{ mt: 2 }}>
                    Advanced Query Configuration
                  </Typography>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Description Note Sequence Numbers'
                    name='description_note_seq'
                    value={settings.description_note_seq}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    variant='outlined'
                    type='text'
                    placeholder='e.g., 30,31,32,33,34'
                    error={!!descriptionNoteSeqError}
                    helperText={descriptionNoteSeqError || (!isAdmin ? 'Only admins can change description note sequence numbers' : 'Comma-separated sequence numbers for product description notes. Multiple notes will be combined with double line breaks.')}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Document Endpoint'
                    name='document_endpoint'
                    value={settings.document_endpoint}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change document endpoint' : 'API endpoint for fetching documents'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    label='Product Variant Field Name'
                    name='product_variant_name'
                    value={settings.product_variant_name}
                    onChange={handleSettingsChange}
                    fullWidth
                    disabled={syncInProgress || !isAdmin}
                    required
                    variant='outlined'
                    helperText={!isAdmin ? 'Only admins can change product variant field name' : 'The field name used for product variants in your Shopify store'}
                    sx={{ mb: 2 }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant='caption' color='textSecondary' sx={{ m: 1, display: 'block' }}>
                    Note: 'Product Variant Field Name':  is only used for newly created products in Shopify and is not applied when updating products.
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', sm: 'row', md: 'row', lg: 'row' } }}>
                    <Box>
                      <Typography variant='subtitle1' color='primary'>
                        Extra Configurations
                      </Typography>
                      <Typography variant='body2' color='textSecondary'>
                        Configure metafield mapping for note sequences. Each configuration will create a separate metafield.
                      </Typography>
                    </Box>
                    {isAdmin ? (
                      <>
                        <Button
                          variant='outlined'
                          size='small'
                          startIcon={<AddIcon />}
                          onClick={addExtraConfiguration}
                          disabled={syncInProgress || !isAdmin}
                          sx={{
                            alignSelf: { xs: 'flex-end', sm: 'flex-end', md: 'flex-end', lg: 'flex-end' }
                          }}
                        >
                          Add
                        </Button>
                      </>
                    ) : (<></>)}
                  </Box>

                  <TableContainer component={Paper} variant='outlined'>
                    <Table size='small'>
                      <TableHead>
                        <TableRow>
                          <TableCell>Note Sequence</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Metafield Namespace</TableCell>
                          <TableCell>Metafield Key</TableCell>
                          {isAdmin ? (<TableCell align='center'>Actions</TableCell>) : (<></>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {settings.extra_configurations && settings.extra_configurations.map((config, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={config.note_seq_number}
                                onChange={(e) => handleExtraConfigurationChange(index, 'note_seq_number', e.target.value)}
                                placeholder="e.g., 75"
                                fullWidth
                                disabled={syncInProgress || !isAdmin}
                                sx={{
                                  mb: { xs: '0px !important' },
                                  my: { xs: '20px !important', lg: '10px !important' },
                                  width: { xs: '100px !important', sm: '100% !important' }
                                }}
                                error={!!errors[index]} // <-- MUI will show red outline if true
                                helperText={errors[index] || ''} // <-- Display error message if exists
                                required
                              />
                            </TableCell>
                            <TableCell>
                              <Select
                                size='small'
                                value={config.type || 'pdf'}
                                onChange={(e) => handleExtraConfigurationChange(index, 'type', e.target.value)}
                                fullWidth
                                disabled={syncInProgress || !isAdmin}
                                required
                              >
                                <MenuItem value='pdf'>PDF</MenuItem>
                                <MenuItem value='text'>Text</MenuItem>
                              </Select>
                            </TableCell>
                            <TableCell>
                              <TextField
                                size='small'
                                value={config.metafield_namespace}
                                onChange={(e) => handleExtraConfigurationChange(index, 'metafield_namespace', e.target.value)}
                                placeholder='e.g., custom'
                                fullWidth
                                disabled={syncInProgress || !isAdmin}
                                sx={{
                                  mb: { xs: '0px !important' },
                                  my: { xs: '20px !important', lg: '10px !important' },
                                  width: { xs: '100px !important', sm: '100% !important' }
                                }}
                                required
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size='small'
                                value={config.metafield_key}
                                onChange={(e) => handleExtraConfigurationChange(index, 'metafield_key', e.target.value)}
                                placeholder='e.g., my_metafield'
                                fullWidth
                                disabled={syncInProgress || !isAdmin}
                                sx={{
                                  mb: { xs: '0px !important' },
                                  my: { xs: '20px !important', lg: '10px !important' },
                                  width: { xs: '100px !important', sm: '100% !important' }
                                }}
                                required
                              />
                            </TableCell>
                            {isAdmin ? (<TableCell align='center'>
                              <IconButton
                                size='small'
                                color='error'
                                onClick={() => removeExtraConfiguration(index)}
                                disabled={syncInProgress || !isAdmin}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>) : (<></>)}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <Box display='flex' justifyContent='flex-end' mt={2}>
                    {isAdmin ? (
                      <>
                        <Button
                          type='submit'
                          variant='contained'
                          color='primary'
                          startIcon={updateSettingsMutation.isPending ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
                          disabled={syncInProgress || !isAdmin || Object.keys(errors).length > 0 || updateSettingsMutation.isPending || descriptionNoteSeqError}
                        >
                          {updateSettingsMutation.isPending ? 'Saving...' : 'Save Settings'}
                        </Button>
                      </>
                    ) : (<></>)}
                  </Box>
                </Grid>
              </Grid>
            </form>
          )}
        </Paper>
        <ConfirmDialog
          open={showResetDialog}
          onClose={() => setShowResetDialog(false)}
          onCancel={() => setShowResetDialog(false)}
          onConfirm={handleConfirmReset}
          confirmColor='warning'
          title='Reset Settings to Default'
          message='Are you sure you want to reset all Shopify to ERP customer sync settings to their default values? This action cannot be undone.'
          confirmText='Reset to Default'
          cancelText='Cancel'
          severity='warning'
          isLoading={resetSettingsMutation.isPending}
        />
      </Grid>
    </Grid >
  );
}
